export const AIR_BRAKE_INSPECTION_STEPS = [
  {
    id: 1,
    category: "Important Reminders",
    title: "Pre-Inspection Setup",
    description: "Ensure the bus is properly prepared before starting the inspection.",
    steps: [
      "Park the bus on level ground",
      "Block the wheels to prevent movement",
      "Ensure the floor is clean and safe to walk on",
      "Wear appropriate safety gear",
      "Make sure you have proper lighting to see all components"
    ],
    safetyTip: "Always block the wheels with chocks before starting any brake inspection.",
    passCriteria: "All setup conditions are met"
  },
  {
    id: 2,
    category: "Steering the Bus",
    title: "Steering & Service Brake Check",
    description: "Check for four-wheel steering capability and service brake function.",
    steps: [
      "Check if the bus has four-wheel steering or two-wheel steering",
      "Turn the steering wheel and observe response",
      "Apply service brake and listen for proper engagement",
      "Release service brake and verify it disengages completely",
      "Ensure the steering mechanism moves smoothly"
    ],
    readings: {
      steeringType: "Should indicate 2-wheel or 4-wheel steering",
      brakeResponse: "Should be immediate and smooth",
      releaseTime: "Should fully release within 2 seconds"
    },
    safetyTip: "Listen for unusual sounds during brake application. Any grinding or squealing indicates a problem.",
    passCriteria: "Steering responds smoothly and brakes engage/disengage properly"
  },
  {
    id: 3,
    category: "Governor Cutoff",
    title: "Governor Cutoff Pressure Check",
    description: "Verify the governor cutoff pressure is within safe operating range.",
    steps: [
      "Locate the air pressure gauge on the dashboard",
      "Start the engine and allow pressure to build",
      "Observe when the compressor cuts out (governor cutoff)",
      "Governor cutoff should occur between 100-120 psi",
      "Record the exact cutoff pressure",
      "Listen for the distinctive 'pop' sound when cutoff occurs"
    ],
    readings: {
      governorCutoff: "100-120 psi (standard range)",
      normal: "100-120 psi",
      warning: "Below 100 psi or above 120 psi"
    },
    safetyTip: "If governor cutoff is outside normal range, do not operate the bus. Report to maintenance immediately.",
    passCriteria: "Cutoff pressure is between 100-120 psi"
  },
  {
    id: 4,
    category: "Governor Cutoff",
    title: "Governor Pressure Movement Check",
    description: "Verify the governor maintains proper air pressure during operation.",
    steps: [
      "After governor cuts off at 100-120 psi, release the parking brake",
      "Observe pressure movements as you move the brake as needed",
      "Pressure should stay within normal range",
      "Governor should cut back in when pressure drops below 85 psi",
      "Ensure pressure does not rise above 120 psi during operation",
      "Pressure fluctuation should be smooth and controlled"
    ],
    readings: {
      cutIn: "Should cut in around 85-90 psi",
      range: "Maintain 100-120 psi while operating",
      fluctuation: "Should be gradual, not sudden drops"
    },
    safetyTip: "Sudden pressure drops indicate a leak. Stop driving and find the leak before continuing.",
    passCriteria: "Pressure stays in range and governor cycles properly"
  },
  {
    id: 5,
    category: "Air Leakage Check",
    title: "System Air Leakage Test",
    description: "Check for air leaks in the brake system.",
    steps: [
      "With engine running and brakes at normal operating pressure (100-120 psi)",
      "Turn off the engine",
      "Read the pressure gauge after 1 minute",
      "Record the pressure drop",
      "A 4-5 psi drop in 1 minute is acceptable",
      "Any drop greater than 5 psi in 1 minute indicates a leak",
      "Listen for hissing sounds indicating air escape",
      "Visually inspect all brake lines and connections for signs of air leaks"
    ],
    readings: {
      acceptable: "Maximum 4-5 psi drop in 1 minute",
      warning: "More than 5 psi drop indicates a leak"
    },
    safetyTip: "If air leakage exceeds acceptable levels, do not drive. The brakes may fail.",
    passCriteria: "Air leakage is 5 psi or less per minute"
  },
  {
    id: 6,
    category: "Parking Brake Test",
    title: "Parking Brake Engagement Test",
    description: "Verify the parking brake holds the bus securely.",
    steps: [
      "Place the transmission in Neutral (or Park if automatic)",
      "Apply the parking brake fully",
      "Try to move the bus forward gently",
      "Bus should not move at all",
      "Release the parking brake",
      "Bus should move freely when transmission is engaged"
    ],
    safetyTip: "Parking brake must hold the bus on even slight grades. If it slips, report immediately.",
    passCriteria: "Bus does not move when parking brake is applied"
  },
  {
    id: 7,
    category: "Service Brake Test",
    title: "Service Brake Operation Check",
    description: "Test the main braking system for proper function.",
    steps: [
      "With engine running, press the service brake pedal",
      "Brake should engage smoothly and progressively",
      "Pressure gauge should show drop in air pressure",
      "Release brake pedal fully",
      "Pressure should return to normal operating range",
      "Brake pedal should return to rest position automatically",
      "No sponginess or delayed response should be felt"
    ],
    readings: {
      pressure: "Should drop when brake applied",
      response: "Should be immediate and firm",
      recovery: "Should fully recover pressure within 5 seconds"
    },
    safetyTip: "If the service brake feels soft or spongy, do not drive. Air may be in the brake lines.",
    passCriteria: "Service brake responds immediately and holds pressure properly"
  },
  {
    id: 8,
    category: "Articulated Bus Checks",
    title: "Articulated Connection & Brakes",
    description: "For articulated buses, check the connection between front and rear sections.",
    steps: [
      "Visually inspect the kingpin and fifth wheel connection",
      "Check for any visible damage or wear",
      "Ensure connection is tight and secure",
      "Apply service brake and listen for unified braking sound",
      "Both sections should brake together simultaneously",
      "No lag between front and rear braking"
    ],
    safetyTip: "A loose connection can cause the rear section to jackknife. Check this carefully.",
    passCriteria: "Connection is secure and both sections brake together"
  },
  {
    id: 9,
    category: "Straight Bus Checks",
    title: "Straight Bus Brake Test",
    description: "For straight (non-articulated) buses, perform this specific brake check.",
    steps: [
      "Apply service brake at low speed (5 mph or less)",
      "Bus should stop smoothly without pulling to either side",
      "Apply parking brake while moving slowly",
      "Bus should come to a stop gradually",
      "No excessive noise or vibration during braking"
    ],
    safetyTip: "If bus pulls to one side when braking, brake balance is off. Do not drive.",
    passCriteria: "Bus stops smoothly and evenly on both sides"
  },
  {
    id: 10,
    category: "Load Adjustment",
    title: "Loaded Bus Brake Check",
    description: "Verify brake performance with a full passenger load.",
    steps: [
      "Have bus at normal operating capacity (passengers or equivalent weight)",
      "Apply service brake at low speed",
      "Ensure braking performance is still smooth and adequate",
      "Pressure gauges should show normal readings",
      "No unusual sounds or vibrations with load"
    ],
    safetyTip: "Brake performance should not degrade significantly with a full load.",
    passCriteria: "Brakes function properly with a full load"
  }
];

export const INSPECTION_MODES = {
  GUIDED: "guided",
  PRACTICE: "practice",
  QUIZ: "quiz",
  HISTORY: "history"
};

export const getStepById = (id) => {
  return AIR_BRAKE_INSPECTION_STEPS.find(step => step.id === id);
};

export const getAllSteps = () => {
  return AIR_BRAKE_INSPECTION_STEPS;
};

export const getStepsByCategory = (category) => {
  return AIR_BRAKE_INSPECTION_STEPS.filter(step => step.category === category);
};
